/**
 * Promote-a-service-to-IBS form (P103) -- the IBS identification entry
 * pathway. Picks a CMDB service, captures the outcome category +
 * rationale (+ sovereign flag), and raises an IBS candidate that flows
 * into the existing Derive(Engine) + sign-off machinery.
 */
import { useToast } from "shared/toast/ToastProvider";
import { Arrow } from "shared/components/Arrow";
import { classificationInSentence } from "shared/vocab/workflowState";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { IBS_OUTCOME_CATEGORIES } from "shared/api/types";
import { usePromotableServices, usePromoteService } from "twin/api/ibs_admission_hooks";
import { canActOn } from "shared/journey/authority";
import { useMe } from "shared/api/useMe";
import { CLASSIFICATION_RANK, isInternalTool } from "twin/ibs/internalTools";

/** Which classifications make a plausible IBS candidate, best first. */

export function PromoteServiceForm({ onDone }: { onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const me = useMe();
  const services = usePromotableServices();
  const promote = usePromoteService();
  // F -- the list mixed the bank's customer services with the canteen
  // booking system and the internal wiki, in one flat alphabetical run.
  // Everything unpromoted is TECHNICALLY promotable, so hiding the tail
  // would be the product deciding for the bank; ordering it is not.
  // What the estate already knows -- how each service is classified --
  // is what separates a plausible candidate from a joke, so the
  // plausible ones lead and each says which it is.
  const ranked = [...(services.data?.services ?? [])].sort((a, b) => {
    const rank = CLASSIFICATION_RANK[a.classification] ?? 9;
    const other = CLASSIFICATION_RANK[b.classification] ?? 9;
    return rank !== other ? rank - other : a.name.localeCompare(b.name);
  });
  /* ROUND 15 H7 -- ordering was not enough: the canteen booking system
     and the internal wiki were still offered for promotion on the same
     list as the bank's customer services. Held behind a toggle: still
     the bank's to promote, not the product's to hide. */
  const [showInternal, setShowInternal] = useState(false);
  const internal = ranked.filter((s) => isInternalTool(s));
  const options = showInternal ? ranked : ranked.filter((s) => !isInternalTool(s));

  /*
   * A1 -- WHY the picker is empty, because the three causes need three
   * different moves from the reader.
   *
   * The old branch was `options.length === 0 && !services.isLoading`,
   * which rendered a failed request and a seat that may not ask with
   * the same sentence as a register nobody has loaded. A refusal shown
   * as an absence sends the reader to fix the wrong thing -- the
   * failure this round found in five other places.
   */
  const emptyReason: "loading" | "error" | "none-loaded" | "all-taken" | null =
    services.isLoading
      ? "loading"
      : services.isError
        ? "error"
        : options.length > 0
          ? null
          : (services.data?.total_services ?? 0) === 0
            ? "none-loaded"
            : "all-taken";

  /* G4 -- the same authority rule the journey spine draws, not a
     second copy of it. Loading the service register is admin-gated, so
     `canActOn("steward", ...)` is the question and `is_admin` is the
     only way through it. */
  const canLoadServices = canActOn("steward", me.data);

  const [serviceId, setServiceId] = useState("");
  const [outcome, setOutcome] = useState<string>(IBS_OUTCOME_CATEGORIES[0]);
  const [rationale, setRationale] = useState("");
  const [sovereign, setSovereign] = useState(false);
  const [stream, setStream] = useState<string>("catalogue");

  const canSubmit = serviceId !== "" && rationale.trim() !== "" && !promote.isPending;

  /* The same sentence the empty state shows, so the disabled button
     and the field above it give one account of the situation rather
     than two. */
  const disabledReason =
    emptyReason === "error"
      ? t("modules.twin.ibsAdmission.promote.servicesUnavailable")
      : emptyReason === "none-loaded"
        ? t("modules.twin.ibsAdmission.promote.noServicesLoaded")
        : emptyReason === "all-taken"
          ? t("modules.twin.ibsAdmission.promote.allPromoted", {
              promoted: services.data?.promoted_count ?? 0,
              outOfScope: services.data?.out_of_scope_count ?? 0,
              total: services.data?.total_services ?? 0,
            })
          : undefined;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    promote.mutate(
      {
        serviceId,
        outcomeCategory: outcome,
        rationale,
        sovereignFlag: sovereign,
        provenanceStream: stream,
      },
      {
        // D1 -- the form closed and nothing said whether a candidate
        // had been raised, so the operator pressed it again. Say what
        // happened, name the thing, and say what has to happen next
        // for it to become an IBS.
        onSuccess: () => {
          const name =
            options.find((o) => o.id === serviceId)?.name ??
            t("modules.twin.ibsAdmission.promote.theService");
          toast.push({
            tone: "success",
            message: t("modules.twin.ibsAdmission.promote.done", { name }),
          });
          setServiceId("");
          setRationale("");
          setSovereign(false);
          setStream("catalogue");
          onDone();
        },
        onError: (err) =>
          toast.push({
            tone: "error",
            message: t("modules.twin.ibsAdmission.promote.failed"),
            detail: err.detail,
          }),
      },
    );
  };

  const STREAMS = ["catalogue", "nbs", "regulator_named"];

  return (
    <form
      data-testid="promote-form"
      onSubmit={onSubmit}
      className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4"
    >
      <h2 className="font-display text-lg text-slate-900">
        {t("modules.twin.ibsAdmission.promote.heading")}
      </h2>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("modules.twin.ibsAdmission.promote.serviceLabel")}
        </span>
        {emptyReason === "loading" ? (
          <span className="text-sm text-slate-400">{t("common.loading")}</span>
        ) : emptyReason !== null ? (
          <span
            data-testid="promote-no-services"
            data-reason={emptyReason}
            className="flex flex-col gap-1 text-sm text-slate-600"
          >
            <span>
              {emptyReason === "error"
                ? t("modules.twin.ibsAdmission.promote.servicesUnavailable")
                : emptyReason === "none-loaded"
                  ? t("modules.twin.ibsAdmission.promote.noServicesLoaded")
                  : t("modules.twin.ibsAdmission.promote.allPromoted", {
                      promoted: services.data?.promoted_count ?? 0,
                      outOfScope: services.data?.out_of_scope_count ?? 0,
                      total: services.data?.total_services ?? 0,
                    })}
            </span>
            {/* ROUND 13 G4 -- round 12's P1 is answered in full: the
                panel names the reason, the seat, the rule and the
                route. Then the route refused her.

                "Load services" landed on /admin/data-import, which is
                admin-gated, and the panel already knows her roles --
                so it was offering the one seat that cannot take it a
                door that would close in her face. Loading the register
                is the data steward's step; where the reader does not
                hold it, say so and send her somewhere she can read
                what the step waits on, rather than somewhere she will
                be refused. */}
            {emptyReason === "none-loaded" &&
              (canLoadServices ? (
                <Link
                  data-testid="promote-load-services"
                  to="/admin/data-import"
                  className="font-semibold text-brand-brassDark underline underline-offset-2"
                >
                  {t("modules.twin.ibsAdmission.promote.noServicesLoadedCta")} <Arrow />
                </Link>
              ) : (
                <span
                  data-testid="promote-load-services-not-yours"
                  className="flex flex-col gap-1"
                >
                  <span>
                    {t("modules.twin.ibsAdmission.promote.loadIsNotYourSeat")}
                  </span>
                  <Link
                    data-testid="promote-load-services-journey"
                    to="/admin/setup"
                    className="font-semibold text-brand-brassDark underline underline-offset-2"
                  >
                    {t("modules.twin.ibsAdmission.promote.loadSeeTheStep")} <Arrow />
                  </Link>
                </span>
              ))}
          </span>
        ) : (
          <select
            data-testid="promote-service-select"
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1.5"
          >
            <option value="">
              {t("modules.twin.ibsAdmission.promote.servicePlaceholder")}
            </option>
            {(options.length === 0 && internal.length > 0 ? internal : options).map(
              (s) => (
                <option key={s.id} value={s.id}>
                  {t("modules.twin.ibsAdmission.promote.serviceOption", {
                    name: s.name,
                    code: s.code,
                    // Round 9 E8 -- the option reads as a sentence
                    // ("Z payments (svc.pay) -- supports a business
                    // service"), so it takes the sentence form. The
                    // badge form belongs on a badge.
                    classification: classificationInSentence(s.classification),
                  })}
                </option>
              ),
            )}
          </select>
        )}
        {internal.length > 0 && options.length > 0 && (
          <button
            type="button"
            data-testid="promote-internal-toggle"
            onClick={() => setShowInternal((v) => !v)}
            className="mt-1 text-[11px] text-brand-brassDark hover:underline"
          >
            {showInternal
              ? t("modules.twin.ibsAdmission.promote.hideInternal")
              : t("modules.twin.ibsAdmission.promote.showInternal", {
                  count: internal.length,
                })}
          </button>
        )}
        <span className="text-[10px] text-slate-400">
          {t("modules.twin.ibsAdmission.promote.eligibilityRule")}
        </span>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("modules.twin.ibsAdmission.promote.outcomeLabel")}
        </span>
        <select
          data-testid="promote-outcome-select"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          className="rounded border border-slate-300 bg-white px-2 py-1.5"
        >
          {IBS_OUTCOME_CATEGORIES.map((oc) => (
            <option key={oc} value={oc}>
              {t(`modules.twin.ibsAdmission.promote.outcome.${oc}`)}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("modules.twin.ibsAdmission.promote.rationaleLabel")}
        </span>
        <textarea
          dir="auto"
          data-testid="promote-rationale"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          rows={3}
          placeholder={t("modules.twin.ibsAdmission.promote.rationalePlaceholder")}
          className="rounded border border-slate-300 bg-white px-2 py-1.5"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("modules.twin.ibsAdmission.promote.streamLabel")}
        </span>
        <select
          data-testid="promote-stream-select"
          value={stream}
          onChange={(e) => setStream(e.target.value)}
          className="rounded border border-slate-300 bg-white px-2 py-1.5"
        >
          {STREAMS.map((s) => (
            <option key={s} value={s}>
              {t(`modules.twin.ibsAdmission.promote.stream.${s}`)}
            </option>
          ))}
        </select>
        <span className="text-[10px] text-slate-400">
          {t("modules.twin.ibsAdmission.promote.streamHint")}
        </span>
      </label>

      <label className="flex items-center gap-2 text-sm">
        <input
          data-testid="promote-sovereign"
          type="checkbox"
          checked={sovereign}
          onChange={(e) => setSovereign(e.target.checked)}
        />
        <span>{t("modules.twin.ibsAdmission.promote.sovereignLabel")}</span>
      </label>

      {promote.isError && (
        <p data-testid="promote-error" className="text-sm text-rag-red">
          {promote.error.detail}
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          data-testid="promote-submit"
          disabled={!canSubmit}
          /* A1 -- the button was disabled with title=None, a regression
             against the standard the MVB button three clicks away
             already met. A disabled control that cannot say why is a
             refusal with no reason attached. */
          title={
            emptyReason !== null && emptyReason !== "loading"
              ? disabledReason
              : serviceId === ""
                ? t("modules.twin.ibsAdmission.promote.needService")
                : rationale.trim() === ""
                  ? t("modules.twin.ibsAdmission.promote.needRationale")
                  : undefined
          }
          className="rounded bg-brand-brassDark px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
        >
          {promote.isPending
            ? t("modules.twin.ibsAdmission.promote.submitting")
            : t("modules.twin.ibsAdmission.promote.submit")}
        </button>
        <button
          type="button"
          data-testid="promote-cancel"
          onClick={onDone}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700"
        >
          {t("modules.twin.ibsAdmission.promote.cancel")}
        </button>
      </div>
    </form>
  );
}
